import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { client } from '../api/client';

interface Recommendation { type: string; priority: string; topicId: number; topicName: string; topicNameCn?: string; reason: string; reasonCn?: string; }

export function AIRecommendPage() {
  const { user } = useAuthStore();
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    client.api.fetch('/api/public/ai/recommend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id }),
    })
      .then(r => r.json())
      .then(d => { setRecs(d.data?.recommendations || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user]);

  if (!user) return <div className="min-h-[60vh] flex items-center justify-center"><p className="text-slate-600">请先登录</p></div>;

  const typeConfig: Record<string, { icon: string; color: string; label: string }> = {
    review: { icon: '🔄', color: 'border-red-200 bg-red-50', label: '需要复习' },
    mistakes: { icon: '❌', color: 'border-orange-200 bg-orange-50', label: '错题回顾' },
    new: { icon: '✨', color: 'border-blue-200 bg-blue-50', label: '新主题' },
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-2xl mb-4">
          <span className="text-3xl">🤖</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-800 mb-2">AI 学习推荐</h1>
        <p className="text-slate-500">根据你的学习数据，AI 为你推荐最适合的学习内容</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" /></div>
      ) : recs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <p className="text-4xl mb-3">🎉</p>
          <p className="text-lg font-medium text-slate-700">暂无推荐</p>
          <p className="text-slate-500 text-sm">开始做题后，AI 会根据你的表现给出个性化推荐</p>
        </div>
      ) : (
        <div className="space-y-4">
          {recs.map((rec, i) => {
            const cfg = typeConfig[rec.type] || typeConfig.new;
            return (
              <div key={i} className={`rounded-xl border p-5 ${cfg.color} transition-all hover:shadow-md`}>
                <div className="flex items-start gap-4">
                  <span className="text-2xl">{cfg.icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rec.priority === 'high' ? 'bg-red-200 text-red-700' : 'bg-blue-200 text-blue-700'}`}>
                        {rec.priority === 'high' ? '优先' : '推荐'}
                      </span>
                      <span className="text-xs text-slate-500">{cfg.label}</span>
                    </div>
                    <h3 className="font-semibold text-slate-800">{rec.topicNameCn || rec.topicName}</h3>
                    <p className="text-sm text-slate-600 mt-1">{rec.reasonCn || rec.reason}</p>
                  </div>
                  <a href={`/practice/${rec.topicId}`}
                    className="px-4 py-2 bg-white rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 flex-shrink-0">
                    开始练习
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
